import React, { useEffect, useRef } from "react";
import { keys } from "lodash";
import { Filters } from "@/services/api/types";
import { ColumnData } from "@/entities/dataTables/types";
import {
  hasActiveFilterValue,
  isViewAppliedFilters
} from "@/components/controls/ToolbarActions/utils";

const activeFilterKeys = (filters: Filters) =>
  keys(filters).filter((key) =>
    hasActiveFilterValue(filters[key as keyof Filters])
  );

// Shows the column of a filter the user just added. Filters a view applies
// are not additions: the view's own columns say what is visible.
export const useRevealFilteredColumns = (
  filters: Filters,
  columnsState: ColumnData[] | undefined,
  setColumnsState:
    | React.Dispatch<React.SetStateAction<ColumnData[]>>
    | undefined,
  defaultColumnsState: ColumnData[]
) => {
  const prevFiltersRef = useRef<Filters>(filters);

  useEffect(() => {
    if (isViewAppliedFilters(filters)) {
      prevFiltersRef.current = filters;
      return;
    }

    if (!columnsState || !setColumnsState || columnsState.length === 0) {
      return;
    }

    const prevFilters = prevFiltersRef.current;
    prevFiltersRef.current = filters;

    const prevActiveFilterKeys = activeFilterKeys(prevFilters);
    const newFilterKeys = activeFilterKeys(filters).filter(
      (key) => !prevActiveFilterKeys.includes(key)
    );

    if (newFilterKeys.length === 0) {
      return;
    }

    // Find columns that are newly filtered but not visible
    const hiddenFilteredColumns: string[] = [];
    const columnsToAdd: ColumnData[] = [];

    newFilterKeys.forEach((filterKey) => {
      const column = columnsState.find((col) => col.id === filterKey);
      if (column) {
        // Column exists in columnsState - check if it's hidden
        if (column.isVisible === false) {
          hiddenFilteredColumns.push(filterKey);
        }
      } else {
        // Column doesn't exist in columnsState - check if it exists in defaultColumnsState
        const defaultColumn = defaultColumnsState.find(
          (col) => col.id === filterKey
        );
        if (defaultColumn) {
          // Add the column from defaults with isVisible = true
          columnsToAdd.push({ ...defaultColumn, isVisible: true });
        }
      }
    });

    // If there are hidden columns or columns to add, update columnsState
    if (hiddenFilteredColumns.length > 0 || columnsToAdd.length > 0) {
      setColumnsState((currentColumns) => {
        // First, make hidden columns visible
        let updatedColumns = currentColumns.map((col) =>
          hiddenFilteredColumns.includes(col.id)
            ? { ...col, isVisible: true }
            : col
        );
        // Then, add any new columns that weren't in columnsState
        if (columnsToAdd.length > 0) {
          updatedColumns = [...updatedColumns, ...columnsToAdd];
        }
        return updatedColumns;
      });
    }
  }, [filters, columnsState, setColumnsState, defaultColumnsState]);
};
